"use client";
import { useRef } from "react";
import { motion, useInView } from "motion/react";
import { Link } from "@/i18n/navigation";
import AnimatedCard from "@/components/ui/animated-card";
import BorderWrapper from "@/components/ui/border-wrapper";
import { ArrowRight } from "lucide-react";
import { useTranslations } from "next-intl";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";

const containerVariants = {
  hidden: { opacity: 1 },
  show: { opacity: 1, transition: { staggerChildren: 0.1 } },
} as const;

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
} as const;

interface BlogPost {
  slug: string;
  title: string;
  description: string;
  date: string;
  readingTime: number;
  tags: string[];
}

const LatestBlog = ({ posts }: { posts: BlogPost[] }) => {
  const t = useTranslations("latestBlog");
  const sectionRef = useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, { once: true, margin: "0px 0px -60px 0px" });

  if (posts.length === 0) return null;

  return (
    <section ref={sectionRef} className="section pt-[40px] lg:pt-[60px] pb-[40px] lg:pb-[60px] relative">
      <div
        className="absolute left-[-10%] top-[30%] w-[500px] h-[500px] bg-[linear-gradient(180deg,_rgba(135,82,250,0.06)_0%,_rgba(34,12,81,0)_100%)] rounded-full blur-[120px] pointer-events-none animate-shape-pulse"
      />

      <div className="container relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "show" : "hidden"}
        >
          {/* Header */}
          <motion.div
            className="flex items-end justify-between mb-[30px] lg:mb-[40px]"
            variants={itemVariants}
          >
            <h2 className="text-[28px] md:text-[40px] lg:text-[60px] font-semibold leading-[1.2]">
              {t("title")}
            </h2>
            <Link
              href="/blog"
              className="hidden sm:flex items-center gap-[8px] text-[15px] font-light text-foreground/50 hover:text-white transition-colors duration-300 shrink-0 pb-[4px] lg:pb-[12px]"
            >
              {t("viewAll")}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>

          {/* MOBILE + TABLET CAROUSEL */}
          <motion.div className="lg:hidden" variants={itemVariants}>
            <Carousel
              aria-label={t("title")}
              opts={{ align: "center", loop: false }}
              className="w-full [&>div]:!overflow-visible"
            >
              <CarouselContent className="-ml-2 md:-ml-[32px]">
                {posts.map((post) => (
                  <CarouselItem key={post.slug} className="pl-2 md:pl-[32px] basis-[85%] md:basis-3/4">
                    <Link href={`/blog/${post.slug}`} className="block h-full group">
                      <AnimatedCard
                        className="rounded-[18px] h-full"
                        circleClassName="w-[120px] h-[120px] blur-[70px]"
                      >
                        <BorderWrapper
                          wrapperClass="rounded-[18px] h-full"
                          className="rounded-[18px] h-full bg-gradient-to-b from-[#121124] to-[#0e0821] p-[28px] overflow-hidden relative flex flex-col"
                        >
                          <div className="absolute top-0 right-0 w-[200px] h-[200px] bg-theme/10 rounded-full blur-[80px] pointer-events-none animate-shape-pulse" />
                          <div className="flex flex-col flex-1 relative z-10">
                            <div className="flex items-center gap-[8px] mb-[16px]">
                              {post.tags[0] && (
                                <span className="px-[10px] py-[3px] rounded-full text-[11px] font-light border border-white/10 text-foreground/40">
                                  {post.tags[0]}
                                </span>
                              )}
                              <span className="text-[11px] font-light text-foreground/30">
                                {post.readingTime} {t("minRead")}
                              </span>
                            </div>
                            <h3 className="text-[18px] font-semibold leading-[1.3] text-white mb-[12px] group-hover:text-foreground/80 transition-colors duration-300">
                              {post.title}
                            </h3>
                            <p className="text-[14px] font-light leading-[1.7] text-foreground/60 line-clamp-3 flex-1">
                              {post.description}
                            </p>
                            <p className="text-[12px] font-light text-foreground/30 mt-[20px]">
                              {post.date}
                            </p>
                          </div>
                        </BorderWrapper>
                      </AnimatedCard>
                    </Link>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <div className="flex justify-center items-center mt-[30px] gap-[15px]">
                <CarouselPrevious className="static translate-x-0 translate-y-0 w-[50px] h-[50px] rounded-full bg-[#4a3290] text-white disabled:bg-[#15092c] cursor-pointer border-white/5 focus:bg-theme focus:text-white" />
                <CarouselNext className="static translate-x-0 translate-y-0 w-[50px] h-[50px] rounded-full bg-[#4a3290] text-white disabled:bg-[#15092c] cursor-pointer border-white/5 focus:bg-theme focus:text-white" />
              </div>
            </Carousel>
          </motion.div>

          {/* DESKTOP GRID */}
          <div className="hidden lg:grid lg:grid-cols-3 gap-[16px]">
            {posts.map((post) => (
              <motion.div key={post.slug} variants={itemVariants}>
                <Link href={`/blog/${post.slug}`} className="block h-full group">
                  <AnimatedCard
                    className="rounded-[18px] lg:rounded-[22px] h-full"
                    circleClassName="w-[120px] h-[120px] blur-[70px]"
                  >
                    <BorderWrapper
                      wrapperClass="rounded-[18px] lg:rounded-[22px] h-full"
                      className="rounded-[18px] lg:rounded-[22px] h-full bg-gradient-to-b from-[#121124] to-[#0e0821] p-[28px] lg:p-[36px] overflow-hidden relative flex flex-col"
                    >
                      <div className="absolute top-0 right-0 w-[200px] h-[200px] bg-theme/10 rounded-full blur-[80px] pointer-events-none animate-shape-pulse" />
                      <div className="flex flex-col flex-1 relative z-10">
                        <div className="flex items-center gap-[8px] mb-[16px]">
                          {post.tags[0] && (
                            <span className="px-[10px] py-[3px] rounded-full text-[11px] font-light border border-white/10 text-foreground/40">
                              {post.tags[0]}
                            </span>
                          )}
                          <span className="text-[11px] font-light text-foreground/30">
                            {post.readingTime} {t("minRead")}
                          </span>
                        </div>
                        <h3 className="text-[18px] lg:text-[22px] font-semibold leading-[1.3] text-white mb-[12px] group-hover:text-foreground/80 transition-colors duration-300">
                          {post.title}
                        </h3>
                        <p className="text-[14px] lg:text-[15px] font-light leading-[1.7] text-foreground/60 line-clamp-3 flex-1">
                          {post.description}
                        </p>
                        <p className="text-[12px] font-light text-foreground/30 mt-[20px]">
                          {post.date}
                        </p>
                      </div>
                    </BorderWrapper>
                  </AnimatedCard>
                </Link>
              </motion.div>
            ))}
          </div>

          {/* Mobile view all */}
          <motion.div variants={itemVariants} className="sm:hidden mt-[24px]">
            <Link
              href="/blog"
              className="flex items-center gap-[8px] text-[15px] font-light text-foreground/50 hover:text-white transition-colors duration-300"
            >
              {t("viewAll")}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default LatestBlog;
